import { ProFormSwitch } from '@ant-design/pro-components'
import type React from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { useMergedFieldProps } from '../utils/mergeFieldProps'

export type SwitchFieldProps = React.ComponentProps<typeof ProFormSwitch> & {
  /**
   * Invert the boolean value when reading/writing.
   * Useful when the stored value is `disabled` but the UI represents `enabled`.
   */
  inverse?: boolean
}

export function SwitchField(props: SwitchFieldProps) {
  const fieldCtx = useFieldContext<boolean>()
  const { inverse = false, formItemProps, fieldProps, proFieldProps, className, ...rest } = props

  const editable = useEditableFieldMode<boolean>()

  const merged = useMergedFieldProps({
    fieldCtx: fieldCtx as never,
    editable,
    props: {
      ...props,
      layout: props.layout ?? 'horizontal',
    },
    fieldDefaults: {
      fieldProps: {
        unCheckedChildren: editable.mode === 'read' ? 'Nein' : '',
        checkedChildren: editable.mode === 'read' ? 'Ja' : '',
        checked:
          typeof fieldCtx.state.value === 'boolean'
            ? inverse
              ? !fieldCtx.state.value
              : fieldCtx.state.value
            : undefined,
        onChange: (checked: boolean) => fieldCtx.handleChange(inverse ? !checked : checked),
      },
    },
  })

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProFormSwitch
        {...rest}
        label={
          <EditableLabel
            mode={editable.mode}
            enterEdit={editable.enterEdit}
            exitRead={editable.exitRead}
            onUndo={editable.undoAndExit}
            canUndo={editable.canUndo}
          >
            {rest.label}
          </EditableLabel>
        }
        proFieldProps={merged.proFieldProps}
        formItemProps={merged.formItemProps}
        fieldProps={merged.fieldProps}
      />
    </EditableShell>
  )
}
